import {
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactComponent as CloseIcon } from 'assets/icons/close_circle.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { searchBrand } from 'libs/brandfetch/brandfetch';
import { ChangeEvent, FocusEvent, FormEvent, KeyboardEvent, MouseEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutoCompleteBrandSearchProps, Queries, ValueState } from './AutoCompleteBrandSearch.types';

const resultsId = 'autobrand-search-results';
const labelId = 'autobrand-search-label';
const clearButtonId = 'autobrand-search-clear-button';

export const AutoCompleteBrandSearch = ({
  onSubmit,
  onChange,
  onClear,
  debounceTime = 100,
  isDisabled,
  placeholder,
  initialValue,
  errorMessage,
}: AutoCompleteBrandSearchProps) => {
  const { t } = useTranslation('auto_brand', { useSuspense: false, keyPrefix: 'modals.autoBrand' });
  const [value, setValue] = useState<ValueState>({ text: initialValue || '', active: false });
  const [queries, setQueries] = useState<Queries[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const queriesContainerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const domain = queries?.[0]?.domain || value.text;
    if (!domain) return;

    // set active to false for the value but the rest same
    setValue((oldValue) => ({ ...oldValue, active: false }));
    onSubmit({ value: domain, displayName: queries?.[0]?.name });
  };

  const handleClick = (query: Queries) => {
    if (!query?.domain) return;
    onSubmit({ value: query.domain, displayName: query.name || '' });
    setValue({ text: query.domain, active: false });
  };

  const reset = () => {
    setQueries([]);
    setValue({ text: '', active: false });
    setActiveIndex(-1);
    if (onClear) onClear();
    if (inputRef.current) inputRef.current.focus();
  };

  const getQueries = useCallback(async (searchValue: string) => {
    try {
      const data = await searchBrand(searchValue);
      setActiveIndex(-1);
      setQueries(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debounceGetQueries = useDebouncedCallback((searchValue: string) => getQueries(searchValue), debounceTime);

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const target = e.relatedTarget as Node | null;

    // only if its from an element outside of the containerRef
    const isInContainer = containerRef?.current?.contains(target);
    const isInQueriesContainer = queriesContainerRef?.current?.contains(target);

    if (!isInContainer && !isInQueriesContainer) {
      setValue({ text: e.target.value, active: false });
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue({ text: e.target.value, active: true });
    onChange?.(e.target.value);

    if (e.target.value === '') {
      setQueries([]);
    } else {
      setIsLoading(true);
      debounceGetQueries(e.target.value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Don't handle keys if no queries or dropdown is closed
    if (!queries.length || !value.active) return;
    e.stopPropagation();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault(); // Prevent cursor movement
        e.stopPropagation();
        setActiveIndex((prev) => (prev < queries.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault(); // Prevent cursor movement
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : queries.length - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0) {
          e.preventDefault(); // Prevent form submission if item selected
          handleClick(queries[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setValue((prev) => ({ ...prev, active: false }));
        setActiveIndex(-1);
        break;
    }
  };

  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    // if its from the clear button, don't set the value
    if (e.relatedTarget?.id === clearButtonId) return;

    setValue({ text: e.target.value, active: true });
    setActiveIndex(-1);
  };

  const onClick = (e: MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    setValue({ text: target.value, active: true });
    setActiveIndex(-1);
  };

  return (
    <Box ref={containerRef} position='relative'>
      <form onSubmit={handleSubmit} role='search'>
        <FormControl isInvalid={!!errorMessage}>
          <VisuallyHidden>
            <label id={labelId}>{t('searchBrandLabel')}</label>
          </VisuallyHidden>
          <InputGroup>
            <InputLeftElement>
              <IconButton
                size='xs'
                borderRadius='full'
                variant='unstyled'
                icon={
                  <Center>
                    <SearchIcon stroke='var(--vg-colors-upgrade-blue-500' />
                  </Center>
                }
                type='submit'
                aria-label={t('searchBrandLabel')}
                isDisabled={isDisabled}
              />
            </InputLeftElement>
            <Input
              isDisabled={isDisabled}
              placeholder={placeholder}
              value={value.text}
              onChange={onInputChange}
              onBlur={onBlur}
              onFocus={onFocus}
              ref={inputRef}
              onKeyDown={handleKeyDown}
              onClick={onClick}
              role='combobox'
              aria-expanded={value.active}
              aria-controls={resultsId}
              aria-labelledby={labelId}
              aria-activedescendant={activeIndex >= 0 ? `option-${activeIndex}` : undefined}
              aria-autocomplete='list'
              fontSize='sm'
              borderColor={!!errorMessage ? 'red.500' : ''}
            />
            {value.text && (
              <InputRightElement>
                <IconButton
                  onClick={reset}
                  size='xs'
                  borderRadius='full'
                  variant='unstyled'
                  type='button'
                  aria-label={t('clearBrandSearch')}
                  id={clearButtonId}
                  isDisabled={isDisabled}
                  icon={
                    <Center>
                      <CloseIcon stroke='var(--vg-colors-outline-gray)' />
                    </Center>
                  }
                />
              </InputRightElement>
            )}
          </InputGroup>
          {!!errorMessage && <FormErrorMessage fontSize='xs'>{errorMessage}</FormErrorMessage>}
        </FormControl>
      </form>
      {value.active && value.text !== '' && !isDisabled && (
        <QueriesWrapper role='listbox' id={resultsId} ref={queriesContainerRef} data-testid={resultsId}>
          {!!queries.length && (
            <Box display={'flex'} flexDirection={'column'} rowGap={0}>
              {queries.map((query, i) => {
                const isActive = i === activeIndex;
                const onClick = () => handleClick(query);

                return (
                  <Query
                    as='button'
                    key={i}
                    role='option'
                    id={`option-${i}`}
                    tabIndex={-1}
                    aria-selected={isActive}
                    onClick={onClick}
                    outline={isActive ? '2px solid' : 'none'}
                    outlineColor={isActive ? 'upgrade.blue.500' : ''}
                    outlineOffset={isActive ? '2px' : '0'}
                  >
                    <QueryImage>
                      <img src={query.icon} alt='' role='presentation' />
                    </QueryImage>

                    <Text maxW='150px' whiteSpace='nowrap' textOverflow='ellipsis' overflow='hidden' textAlign='left'>
                      {query.name || query.domain}
                    </Text>
                    <Text color='font.200'>{query.domain}</Text>
                  </Query>
                );
              })}
            </Box>
          )}

          {!queries.length && !isLoading && (
            <Text data-testid='not-found' fontSize='sm'>
              {t('notFound')}
            </Text>
          )}
        </QueriesWrapper>
      )}
    </Box>
  );
};

const QueryImage = styled.div`
  box-shadow: var(--vg-colors-font-200) 0px 3px 9px;
  border-radius: var(--vg-radii-md);
  max-height: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  position: relative;
  overflow: hidden;

  img {
    position: absolute;
    height: 100%;
    width: 100%;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
  }
`;

const Query = styled(Box)`
  display: flex;
  border-radius: var(--vg-radii-md);
  font-size: var(--vg-fontSizes-sm);
  column-gap: var(--vg-space-2);
  justify-content: flex-start;
  padding: var(--vg-space-2);
  align-items: center;
  cursor: pointer;
  background: none;
  border: none;

  &:hover {
    background: var(--vg-colors-hover-grayNoAlpha);
  }
`;

const QueriesWrapper = styled.div`
  box-shadow: var(--vg-colors-font-200) 0px 10px 38px -10px;
  border-radius: var(--vg-radii-md);
  margin-top: 0;
  padding: var(--vg-space-2);
  position: absolute;
  max-height: 300px;
  overflow: hidden;
  right: 0;
  left: 0;
  z-index: 100;
`;
